import type { CustomCell, GridCell, TextCell } from '@glideapps/glide-data-grid'
import { GridCellKind } from '@glideapps/glide-data-grid'

import type { DropdownCell, DropdownCellProps, Option } from './DropdownCell'
import type { MoneyCell } from './MoneyCell'
import type { MultiPillCell, PillItem } from './MultiPillCell'
import type { NumberCell, NumberCellProps } from './NumberCell'
import { numberCellEmptyValue } from './NumberCell'
import { hueToPillColorLight, type PillCell, type PillColor, stringToHue } from './PillCell'
import type { DotStatus } from './StatusCell'
import type { StatusCell } from './StatusCell'
import type { ThumbnailCell } from './ThumbnailCell'

const common = {
  allowOverlay: true,
  readonly: false,
}

export function textCell(value: string, opts?: Partial<TextCell>): GridCell {
  return {
    ...common,
    data: value,
    displayData: value,
    kind: GridCellKind.Text,
    ...opts,
  }
}

export function readonlyTextCell(
  value: string,
  hasCursorPointer = true,
  style: TextCell['style'] = 'normal',
): TextCell {
  return {
    allowOverlay: false,
    cursor: hasCursorPointer ? 'pointer' : 'default',
    data: value,
    displayData: value,
    kind: GridCellKind.Text,
    readonly: true,
    style,
  }
}

export function booleanCell(value: boolean, options: Partial<GridCell> = {}): GridCell {
  return {
    ...common,
    ...options,
    allowOverlay: false,
    data: value,
    kind: GridCellKind.Boolean,
  }
}

export function loadingCell(): GridCell {
  return {
    allowOverlay: true,
    copyData: '',
    data: {
      kind: 'spinner-cell',
    },
    kind: GridCellKind.Custom,
  }
}

export function numberCell(
  value: number | typeof numberCellEmptyValue,
  options?: NumberCellProps['options'],
): NumberCell {
  return {
    ...common,
    copyData: value !== numberCellEmptyValue ? value.toString() : '',
    cursor: options?.cursor,
    data: {
      kind: 'number-cell',
      options,
      value,
    },
    kind: GridCellKind.Custom,
  }
}

export function buttonCell(title: string, cb: () => void): CustomCell {
  return {
    allowOverlay: true,
    copyData: '4',
    cursor: 'pointer',
    data: {
      borderColor: ['accentLight', 'accentColor'],
      borderRadius: 9,
      color: 'accentColor',
      kind: 'button-cell',
      onClick: cb,
      title,
    },
    kind: GridCellKind.Custom,
    readonly: true,
  }
}

export function dropdownCell(
  value: Option,
  dataOpts: Pick<DropdownCellProps, 'allowCustomValues' | 'emptyOption'> &
    ({ choices: Option[] } | { update: (text: string) => Promise<Option[]> }),
  opts?: Partial<GridCell>,
): DropdownCell {
  return {
    ...common,
    ...opts,
    copyData: value.label,
    data: {
      ...dataOpts,
      kind: 'dropdown-cell',
      value,
    },
    kind: GridCellKind.Custom,
  }
}

export function thumbnailCell(
  name: string,
  image: string,
  opts?: Partial<GridCell>,
): ThumbnailCell {
  return {
    ...common,
    ...opts,
    copyData: name ?? '',
    data: {
      image,
      kind: 'thumbnail-cell',
      name,
    },
    kind: GridCellKind.Custom,
  }
}

export function statusCell(status: DotStatus, value: string, opts?: Partial<GridCell>): StatusCell {
  return {
    ...common,
    ...opts,
    copyData: value ?? '',
    data: {
      kind: 'status-cell',
      status,
      value,
    },
    kind: GridCellKind.Custom,
  }
}

export function pillCell(
  value: string,
  color: PillColor | null,
  opts?: Partial<GridCell>,
): PillCell {
  const pillColor = color
  const fallbackColor = hueToPillColorLight(stringToHue(value))

  return {
    ...common,
    ...opts,
    copyData: value ?? '',
    data: {
      color: pillColor ?? fallbackColor,
      kind: 'auto-tags-cell',
      value,
    },
    kind: GridCellKind.Custom,
  }
}

export function dateCell(value: string, opts?: Partial<GridCell>): GridCell {
  return {
    ...common,
    ...opts,
    copyData: value ?? '',
    data: {
      kind: 'date-cell',
      value,
    },
    kind: GridCellKind.Custom,
  }
}

export function moneyCell(
  value: number | null,
  currency: string,
  opts?: Partial<GridCell>,
): MoneyCell {
  return {
    ...common,
    ...opts,
    copyData: value?.toString() ?? '',
    data: {
      currency,
      kind: 'money-cell',
      value,
    },
    kind: GridCellKind.Custom,
  }
}

export function multiPillCell(items: readonly PillItem[], opts?: Partial<GridCell>): MultiPillCell {
  return {
    ...common,
    ...opts,
    copyData: items.map((item) => item.label).join(', '),
    data: {
      items,
      kind: 'multi-pill-cell',
    },
    kind: GridCellKind.Custom,
  }
}

export { numberCellEmptyValue }
